/**
 * Shared image upload and type check for camera command and paste handling.
 */

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

/** Image file extensions for paste/drop when MIME type is unreliable. */
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg',
  '.bmp',
  '.ico',
] as const;

export function isImageFile(file: File): boolean {
  if (
    IMAGE_MIME_TYPES.includes(file.type as (typeof IMAGE_MIME_TYPES)[number])
  ) {
    return true;
  }
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return (IMAGE_EXTENSIONS as readonly string[]).includes(ext);
}

export interface UploadImageOptions {
  apiHost: string;
  userEmail: string;
}

export interface UploadImageResult {
  url: string;
  markdown: string;
}

/**
 * Uploads an image file to /api/upload and returns the public URL and markdown snippet.
 * Caller is responsible for toasts and insertion. Throws on validation or HTTP error.
 */
export async function uploadImageFile(
  file: File,
  options: UploadImageOptions,
): Promise<UploadImageResult> {
  if (!isImageFile(file)) {
    throw new Error('File must be an image (JPEG, PNG, GIF, or WebP)');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${options.apiHost}/api/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  const url = result.url as string;
  const altText = file.name.replace(/\.[^/.]+$/, '');
  const markdown = `![${altText}](${url})`;

  return { url, markdown };
}

/**
 * Uploads any file to /api/upload and returns the public URL.
 * Used for pasting non-image files (inserted as links).
 * Throws on HTTP error.
 */
export async function uploadFile(
  file: File,
  options: UploadImageOptions,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${options.apiHost}/api/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  return result.url as string;
}

/**
 * Result of pasting a file: either embedded image markdown or link markdown.
 */
export interface PastedFileResult {
  markdown: string;
}

/**
 * Uploads a pasted file and returns the appropriate markdown to insert.
 * Images are embedded; other files become links.
 */
export async function uploadPastedFile(
  file: File,
  options: UploadImageOptions,
): Promise<PastedFileResult> {
  const url = await uploadFile(file, options);
  const name = file.name.replace(/\.[^/.]+$/, '') || file.name;

  if (isImageFile(file)) {
    return { markdown: `![${name}](${url})` };
  }
  return { markdown: `[${file.name}](${url})` };
}
